/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls
 */

import { apiRequest } from './apiClient';
import { saveTokenInfo } from '../utils/storageUtils';
import { broadcastLogin } from '../utils/authEventBroadcast';
import type {
  ApiError,
  SignInRequest,
  ChangeTemporaryPasswordRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyOTPRequest,
  AuthResponse,
  ProfileResponse,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  VerifyOTPResponse,
  TokenInfo,
  UpdateProfileRequest,
} from './types';

// Re-export types for convenience
export type {
  ApiError,
  User,
  AuthInfo,
  SignInRequest,
  ChangeTemporaryPasswordRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyOTPRequest,
  AuthResponse,
  ProfileResponse,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  VerifyOTPResponse,
} from './types';

// ========================================
// Sign In & Password Management
// ========================================

/**
 * Sign in with email and password
 */
export const signIn = async (
  data: SignInRequest, 
  rememberMe: boolean = false
): Promise<AuthResponse> => {
  try {
    const result = await apiRequest<AuthResponse, SignInRequest>(
      'POST',
      '/account/sign-in',
      {
        email: data.email,
        password: data.password,
      },
      false // No authentication required for sign in
    );
    
    if (result.code === 'PASSWORD_RESET_REQUIRED') {
      throw {
        code: result.code,
        message: result.message || 'Password reset required',
        data: result.data,
        exceptionName: result.exceptionName,
      } as ApiError;
    }
    
    // Save auth info to storage before returning
    const tokenInfo: TokenInfo = {
      accessToken: result.data.accessToken,
      refreshToken: result.data.refreshToken,
    };
    saveTokenInfo(tokenInfo, rememberMe);
    
    // Broadcast login event to all tabs
    broadcastLogin({ email: data.email });
    
    return result;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    // Wrap unexpected errors
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'An unexpected error occurred during sign in',
    } as ApiError;
  }
};

/**
 * Change temporary password (first-time login)
 */
export const changeTemporaryPassword = async (
  data: ChangeTemporaryPasswordRequest,
  rememberMe: boolean = false
): Promise<AuthResponse> => {
  try {
    const result = await apiRequest<AuthResponse, ChangeTemporaryPasswordRequest>(
      'PUT',
      '/account/password/change-temporary-password',
      {
        email: data.email,
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
        currentPassword: data.currentPassword,
      },
      false // No authentication required for changing temporary password
    );
    
    // Save auth info to storage before returning
    const tokenInfo: TokenInfo = {
      accessToken: result.data.accessToken,
      refreshToken: result.data.refreshToken,
    };
    saveTokenInfo(tokenInfo, rememberMe);
    
    // Broadcast login event to all tabs
    broadcastLogin({ email: data.email });
    
    return result;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    // Wrap unexpected errors
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'An unexpected error occurred during password change',
    } as ApiError;
  }
};

/**
 * Change password for an authenticated user
 */
export const changePassword = async (
  data: ChangePasswordRequest
): Promise<{ data: any; code: string; message: string }> => {
  try {
    return await apiRequest<{ data: any; code: string; message: string }, ChangePasswordRequest>(
      'POST',
      '/account/password/change',
      {
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
        currentPassword: data.currentPassword,
      },
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'An unexpected error occurred during password change',
    } as ApiError;
  }
};

/**
 * Request password reset (forgot password)
 */
export const forgotPassword = async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
  try {
    return await apiRequest<ForgotPasswordResponse, ForgotPasswordRequest>(
      'POST',
      '/account/password/forgot',
      {
        email: data.email,
      },
      false // No authentication required
    );
  } catch (error) {
    // Re-throw ApiError as-is
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    // Wrap unexpected errors
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'An unexpected error occurred',
    } as ApiError;
  }
};

/**
 * Verify OTP code
 */
export const verifyOTP = async (data: VerifyOTPRequest): Promise<VerifyOTPResponse> => {
  try {
    return await apiRequest<VerifyOTPResponse, VerifyOTPRequest>(
      'POST',
      '/account/password/verify-code',
      {
        email: data.email,
        verificationToken: data.verificationToken,
        verificationCode: data.verificationCode,
      },
      false // No authentication required
    );
  } catch (error) {
    // Re-throw ApiError as-is
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    // Wrap unexpected errors
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'An unexpected error occurred',
    } as ApiError;
  }
};

/**
 * Reset password with verification code
 */
export const resetPassword = async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  try {
    return await apiRequest<ResetPasswordResponse, ResetPasswordRequest>(
      'POST',
      '/account/password/reset',
      {
        email: data.email,
        verificationToken: data.verificationToken,
        verificationCode: data.verificationCode,
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
      },
      false // No authentication required
    );
  } catch (error) {
    // Re-throw ApiError as-is
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    // Wrap unexpected errors
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'An unexpected error occurred',
    } as ApiError;
  }
};

/**
 * Sign out (server-side revocation + client-side cleanup)
 */
export const signOut = async (): Promise<void> => {
  try {
    // Attempt to revoke session on server
    await apiRequest(
      'POST',
      '/account/sign-out',
      undefined,
      true // Authentication required
    );
    // console.log('✅ Server-side session revoked');
  } catch (error) {
    // If server-side revocation fails, still proceed with client-side cleanup
    console.warn('⚠️  Server-side sign out failed, proceeding with client cleanup:', error);
  }
};

// ========================================
// User Profile (Uses apiClient with auto-refresh)
// ========================================

/**
 * Get current user profile
 * Uses apiRequest with automatic token refresh support
 */
export const getCurrentUser = async (): Promise<ProfileResponse> => {
  try {
    return await apiRequest<ProfileResponse>(
      'GET',
      '/account/profile',
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Update current user profile
 */
export const updateProfile = async (data: UpdateProfileRequest): Promise<ProfileResponse> => {
  try {
    return await apiRequest<ProfileResponse, UpdateProfileRequest>(
      'PUT',
      '/account/profile',
      data,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update profile',
    } as ApiError;
  }
};
